import { useCallback, useEffect, useState } from 'react';
import { apiFetch, resolveMediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { SegmentedTabs } from '@/components/ui/SegmentedTabs';
import { inputClass } from '@/components/ui/inputStyles';
import type { MicroPromoInfoType } from '@/components/MicroPromotionParamsPanel';
import { MicroPromotionEntryDetailModal } from '@/components/MicroPromotionEntryDetailModal';
import { MicroPromotionEntryFormModal } from '@/components/MicroPromotionEntryFormModal';
import { MicroPromotionTaskWebhookPanel } from '@/components/MicroPromotionTaskWebhookPanel';
import { MicroPromotionDaysCell, type WeworkPushSummary } from '@/components/MicroPromotionDaysCell';
import {
  MicroPromotionRobotProgressCell,
  type ProgressAdjustChannel,
  type RobotProgressItem,
} from '@/components/MicroPromotionRobotProgressCell';
import { mpEntryPreviewText } from '@/lib/microPromotionEntryText';
import { cn } from '@/lib/cn';
import {
  mpServingStatusBadgeClass,
  mpServingStatusLabel,
  type MpReviewReason,
} from '@/lib/microPromotionReviewUi';

type ImageRow = {
  image_id?: number;
  path?: string;
  source_url?: string;
  local_path?: string;
  preview_url?: string;
  download_url?: string;
  url?: string;
};

type EntryRow = {
  entry_id: number;
  info_type: string;
  source_label?: string;
  display_text?: string;
  promotion_text?: string;
  contact_phone?: string;
  promotion_channel?: string;
  total_promotion_days?: number;
  moments_planned_days?: number;
  groups_planned_days?: number;
  wework_push?: WeworkPushSummary;
  robot_progress?: RobotProgressItem[];
  promotion_start_date?: string;
  serial_number?: string | null;
  creator_name?: string | null;
  weixin_nickname?: string | null;
  promotion_serving_status?: string;
  serving_status_label?: string;
  review_reason?: MpReviewReason;
  matched_sensitive_words?: string[];
  images?: ImageRow[];
  created_at?: string;
  actual_paid_amount?: number;
};

function formatYuan(n: number): string {
  const v = Number(n);
  if (!Number.isFinite(v)) {
    return '0.00';
  }
  return v.toFixed(2);
}

type WebhookRow = {
  id: number;
  request_id?: string;
  status: string;
  http_code: number;
  error_code?: string | null;
  form_code?: string | null;
  serial_number?: string | null;
  created_at: string;
  result_ok?: boolean;
};

const PER_PAGE = 15;

type Props = {
  areaId: number;
};

export function MicroPromotionEntriesPanel({ areaId }: Props) {
  const [infoType, setInfoType] = useState<MicroPromoInfoType>('graphic');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<EntryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [servingFilter, setServingFilter] = useState<'all' | 'pending_review'>('all');
  const [msg, setMsg] = useState('');
  const [busyId, setBusyId] = useState(0);
  const [webhookItems, setWebhookItems] = useState<WebhookRow[]>([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [defaultMomentsImages, setDefaultMomentsImages] = useState<ImageRow[]>([]);
  const [momentsEnabled, setMomentsEnabled] = useState(true);
  const [detailEntryId, setDetailEntryId] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formEntryId, setFormEntryId] = useState(0);
  const [progressAdjustBusy, setProgressAdjustBusy] = useState('');
  const [cooldownResetBusy, setCooldownResetBusy] = useState('');
  const [weworkPushBusyId, setWeworkPushBusyId] = useState(0);

  function thumbSrc(im: ImageRow): string {
    return (
      im.preview_url ||
      im.download_url ||
      im.url ||
      (im.path ? resolveMediaUrl(im.path) : '') ||
      (im.local_path ? resolveMediaUrl(im.local_path) : '') ||
      im.source_url ||
      ''
    );
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch<{
        ok: boolean;
        items: EntryRow[];
        total: number;
        default_moments_images?: ImageRow[];
        promotion_moments_enabled?: boolean;
      }>('list_micro_promotion_entries', {
        method: 'GET',
        query: {
          area_id: areaId,
          info_type: infoType,
          page,
          per_page: PER_PAGE,
          ...(appliedQ ? { q: appliedQ } : {}),
          ...(servingFilter === 'pending_review' ? { serving_status: 'pending_review' } : {}),
        },
      });
      if (r.ok) {
        setItems(r.items);
        setTotal(r.total);
        setDefaultMomentsImages(Array.isArray(r.default_moments_images) ? r.default_moments_images : []);
        setMomentsEnabled(r.promotion_moments_enabled !== false);
      }
    } finally {
      setLoading(false);
    }
  }, [areaId, infoType, page, appliedQ, servingFilter]);

  const loadWebhook = useCallback(async () => {
    setWebhookLoading(true);
    try {
      const r = await apiFetch<{ ok: boolean; items: WebhookRow[] }>('list_micro_promotion_webhook_events', {
        method: 'GET',
        query: { area_id: areaId, info_type: infoType, page: 1, per_page: 8 },
      });
      if (r.ok) setWebhookItems(r.items);
    } finally {
      setWebhookLoading(false);
    }
  }, [areaId, infoType]);

  useEffect(() => {
    setPage(1);
  }, [infoType, appliedQ, servingFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadWebhook();
  }, [loadWebhook]);

  async function adjustRobotProgress(
    entryId: number,
    robotId: number,
    channel: ProgressAdjustChannel,
    delta: 1 | -1,
  ) {
    const busyKey = `${entryId}:${robotId}:${channel}`;
    setProgressAdjustBusy(busyKey);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; robot_progress?: RobotProgressItem[] }>(
        'adjust_micro_promotion_robot_progress',
        {
          method: 'POST',
          body: JSON.stringify({ entry_id: entryId, robot_id: robotId, channel, delta }),
        },
      );
      if (!r.ok || !Array.isArray(r.robot_progress)) {
        const errMap: Record<string, string> = {
          already_zero: '已完成天数已是 0，无法再减',
          already_completed: '该渠道已完成，无法再增加',
          channel_not_required: '该条目不含此推广渠道',
          site_unavailable: '机器人未绑定站点，无法手动 +1',
        };
        setMsg(errMap[r.err || ''] || '调整失败');
        return;
      }
      setItems((prev) =>
        prev.map((row) => (row.entry_id === entryId ? { ...row, robot_progress: r.robot_progress } : row)),
      );
    } finally {
      setProgressAdjustBusy('');
    }
  }

  async function resetRobotPullCooldown(
    entryId: number,
    robotId: number,
    channel: ProgressAdjustChannel,
    robotName: string,
    pullWait: string,
  ) {
    const label = channel === 'moments' ? '发圈' : '群发';
    const ok = window.confirm(
      `确定跳过「${robotName}」在本条目上的${label}冷却？\n\n当前：${pullWait}\n\n跳过后机器人下次拉取任务时可立即执行；本次执行完成后，仍按原频次（如每 2 天 1 次）计算下一次间隔。`,
    );
    if (!ok) return;
    const busyKey = `${entryId}:${robotId}:${channel}`;
    setCooldownResetBusy(busyKey);
    setMsg('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        already_ready?: boolean;
        robot_progress?: RobotProgressItem[];
      }>('reset_micro_promotion_robot_pull_cooldown', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId, robot_id: robotId, channel }),
      });
      if (!r.ok || !Array.isArray(r.robot_progress)) {
        const errMap: Record<string, string> = {
          already_completed: '该渠道已完成，无需跳过冷却',
          channel_not_required: '该条目不含此推广渠道',
          log_not_found: '尚无执行记录，无法跳过冷却',
        };
        setMsg(errMap[r.err || ''] || '操作失败');
        return;
      }
      setItems((prev) =>
        prev.map((row) => (row.entry_id === entryId ? { ...row, robot_progress: r.robot_progress } : row)),
      );
      setMsg(r.already_ready ? '当前已无冷却，机器人可立即拉取' : '已跳过冷却，机器人下次拉取时可立即执行');
    } finally {
      setCooldownResetBusy('');
    }
  }

  async function pushWeworkNow(entryId: number, pullWait: string) {
    const ok = window.confirm(
      `确定立即向企微群推送条目 #${entryId}？\n\n当前：${pullWait}\n\n将马上通过 Webhook 发送；本次推送完成后，仍按原频次（如每 2 天 1 次）计算下一次间隔。`,
    );
    if (!ok) return;
    setWeworkPushBusyId(entryId);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; wework_push?: WeworkPushSummary }>(
        'push_micro_promotion_wework_now',
        {
          method: 'POST',
          body: JSON.stringify({ entry_id: entryId }),
        },
      );
      if (!r.ok || !r.wework_push) {
        const errMap: Record<string, string> = {
          wework_disabled: '该区域未启用企微 Webhook 推送',
          no_planned: '该条目无企微推送计划',
          already_completed: '企微推送次数已用完',
          not_active: '条目未处于推广中，无法推送',
          out_of_period: '不在推广期内，无法推送',
          empty_content: '推送内容为空',
          no_webhook_url: '未配置 Webhook 地址',
          send_failed: 'Webhook 发送失败，请查看企微推送日志',
          wecom_lib_missing: '服务端缺少企微推送组件',
        };
        setMsg(errMap[r.err || ''] || '推送失败');
        return;
      }
      setItems((prev) =>
        prev.map((row) => (row.entry_id === entryId ? { ...row, wework_push: r.wework_push } : row)),
      );
      setMsg('已向企微群推送；下次推送将按设定频次计算');
    } finally {
      setWeworkPushBusyId(0);
    }
  }

  async function approveReview(entryId: number) {
    setBusyId(entryId);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('approve_micro_promotion_entry_review', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId }),
      });
      if (!r.ok) {
        setMsg(r.detail || '审核通过失败');
        return;
      }
      setMsg('已通过审核，已进入推广队列');
      await load();
    } finally {
      setBusyId(0);
    }
  }

  async function rejectReview(entryId: number) {
    if (!window.confirm('确定拒绝该条审核？拒绝后不会进入推广队列，可编辑正文后重新保存以再次触发审核。')) return;
    setBusyId(entryId);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('reject_micro_promotion_entry_review', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId }),
      });
      if (!r.ok) {
        setMsg(r.detail || '拒绝失败');
        return;
      }
      await load();
    } finally {
      setBusyId(0);
    }
  }

  async function setServing(entryId: number, status: 'active' | 'paused' | 'completed') {
    setBusyId(entryId);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_micro_promotion_entry_serving', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId, promotion_serving_status: status }),
      });
      if (!r.ok) {
        setMsg('操作失败');
        return;
      }
      await load();
    } finally {
      setBusyId(0);
    }
  }

  async function removeEntry(entryId: number) {
    if (!window.confirm('确定删除该条推广信息？不可恢复。')) return;
    setBusyId(entryId);
    try {
      const r = await apiFetch<{ ok: boolean }>('delete_micro_promotion_entry', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId }),
      });
      if (r.ok) await load();
      else setMsg('删除失败');
    } finally {
      setBusyId(0);
    }
  }

  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="space-y-5">
      <SegmentedTabs
        ariaLabel="信息类型"
        value={infoType}
        onChange={(t) => {
          setInfoType(t);
          setAppliedQ('');
          setQ('');
        }}
        items={[
          { id: 'graphic', label: '图文信息' },
          { id: 'recruitment', label: '招聘信息' },
        ]}
      />

      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[12rem] flex-1">
          <span className="mb-1 block text-xs font-medium text-slate-600">关键词</span>
          <input
            className={inputClass}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="推广文案、电话、序号、提交人"
            onKeyDown={(e) => {
              if (e.key === 'Enter') setAppliedQ(q.trim());
            }}
          />
        </label>
        <Button type="button" variant="secondary" onClick={() => setAppliedQ(q.trim())}>
          筛选
        </Button>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          <button
            type="button"
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium',
              servingFilter === 'all' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50',
            )}
            onClick={() => setServingFilter('all')}
          >
            全部
          </button>
          <button
            type="button"
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium',
              servingFilter === 'pending_review' ? 'bg-violet-700 text-white' : 'text-slate-600 hover:bg-slate-50',
            )}
            onClick={() => setServingFilter('pending_review')}
          >
            待审核
          </button>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            setFormEntryId(0);
            setFormOpen(true);
          }}
        >
          添加信息
        </Button>
        {appliedQ ? (
          <Button type="button" variant="ghost" onClick={() => { setQ(''); setAppliedQ(''); }}>
            清除
          </Button>
        ) : null}
      </div>
      {msg ? <p className="text-sm text-rose-600">{msg}</p> : null}

      <Card title={`${infoType === 'graphic' ? '图文' : '招聘'}推广列表`}>
        {loading ? (
          <Spinner label="加载列表" />
        ) : items.length === 0 ? (
          <EmptyState>
            暂无推广信息。可通过「添加信息」手工录入，或由金数据表单 Webhook 自动入库。
          </EmptyState>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[1080px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/90">
                  <th className="whitespace-nowrap px-3 py-3 text-xs font-semibold text-slate-600">编号</th>
                  <th className="min-w-[160px] px-3 py-3 text-xs font-semibold text-slate-600">推广文案</th>
                  <th
                    className="min-w-[100px] px-3 py-3 text-xs font-semibold text-slate-600"
                    title="按区域发圈/群发默认频次，由套餐总天数 T 折算"
                  >
                    <span className="block">推广天数</span>
                    <span className="mt-0.5 block text-[10px] font-normal text-slate-500">发圈 · 群发</span>
                  </th>
                  <th
                    className="min-w-[200px] px-3 py-3 text-xs font-semibold text-slate-600"
                    title="各绑定机器人独立计完成；发圈/群发进度来自推广日志"
                  >
                    任务完成情况
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-xs font-semibold text-slate-600">实付金额</th>
                  <th className="px-3 py-3 text-xs font-semibold text-slate-600">发圈图</th>
                  <th className="whitespace-nowrap px-3 py-3 text-xs font-semibold text-slate-600">状态</th>
                  <th className="sticky right-0 min-w-[140px] border-l border-slate-100 bg-slate-50/95 px-2 py-3 text-right text-xs font-semibold text-slate-600">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((row) => {
                  const text = mpEntryPreviewText(row);
                  const preview = text.length > 64 ? text.slice(0, 64) + '…' : text;
                  const status = row.promotion_serving_status || 'active';
                  const entryImages = row.images ?? [];
                  const useDefaultThumbs =
                    entryImages.length === 0 && momentsEnabled && defaultMomentsImages.length > 0;
                  const thumbs = useDefaultThumbs ? defaultMomentsImages : entryImages;
                  return (
                    <tr key={row.entry_id} className="bg-white hover:bg-slate-50/80">
                      <td className="align-top px-3 py-3">
                        <span className="inline-flex rounded-md bg-teal-50 px-2 py-0.5 font-mono text-xs font-semibold text-teal-900 ring-1 ring-teal-200/70">
                          #{row.entry_id}
                        </span>
                        {row.serial_number ? (
                          <span className="mt-1 block font-mono text-[11px] text-slate-500">{row.serial_number}</span>
                        ) : null}
                        <span className="mt-1 block text-[11px] text-slate-500">{row.source_label || '金数据'}</span>
                      </td>
                      <td className="align-top px-3 py-3">
                        <p className="line-clamp-2 text-[13px] leading-snug text-slate-800" title={text}>
                          {preview || '（无文案）'}
                        </p>
                        {row.contact_phone ? (
                          <p className="mt-1 text-xs text-slate-500">{row.contact_phone}</p>
                        ) : null}
                        <p className="mt-1 text-[11px] text-slate-400">
                          起始 {row.promotion_start_date || '—'}
                          {row.created_at ? ` · ${row.created_at}` : ''}
                        </p>
                      </td>
                      <td className="align-top px-3 py-3">
                        <MicroPromotionDaysCell
                          row={row}
                          editable
                          weworkPushBusy={weworkPushBusyId === row.entry_id}
                          onPushWeworkNow={(pullWait) => void pushWeworkNow(row.entry_id, pullWait)}
                        />
                      </td>
                      <td className="align-top px-3 py-3">
                        <MicroPromotionRobotProgressCell
                          items={row.robot_progress}
                          promotionChannel={row.promotion_channel}
                          editable
                          adjustBusyKey={progressAdjustBusy.startsWith(`${row.entry_id}:`) ? progressAdjustBusy.split(':').slice(1).join(':') : ''}
                          cooldownResetBusyKey={
                            cooldownResetBusy.startsWith(`${row.entry_id}:`)
                              ? cooldownResetBusy.split(':').slice(1).join(':')
                              : ''
                          }
                          onAdjust={(robotId, channel, delta) =>
                            void adjustRobotProgress(row.entry_id, robotId, channel, delta)
                          }
                          onResetCooldown={(robotId, channel) => {
                            const item = row.robot_progress?.find((x) => x.robot_id === robotId);
                            const wait =
                              channel === 'moments' ? item?.moments_pull_wait : item?.groups_pull_wait;
                            if (!wait) return;
                            void resetRobotPullCooldown(
                              row.entry_id,
                              robotId,
                              channel,
                              item?.robot_name || `机器人#${robotId}`,
                              wait,
                            );
                          }}
                        />
                      </td>
                      <td className="align-top px-3 py-3">
                        <span
                          className={cn(
                            'font-mono text-sm tabular-nums',
                            Number(row.actual_paid_amount ?? 0) > 0
                              ? 'font-semibold text-emerald-700'
                              : 'text-slate-400',
                          )}
                        >
                          ¥{formatYuan(Number(row.actual_paid_amount ?? 0))}
                        </span>
                      </td>
                      <td className="align-top px-3 py-3">
                        {thumbs.length > 0 ? (
                          <div>
                            {useDefaultThumbs ? (
                              <p className="mb-1 text-[10px] font-medium text-violet-700">默认图</p>
                            ) : null}
                            <div className="flex gap-1">
                              {thumbs.slice(0, 2).map((im, idx) => {
                                const src = thumbSrc(im);
                                return (
                                  <a
                                    key={im.image_id ?? im.path ?? idx}
                                    href={im.download_url || src}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={cn(
                                      'block h-12 w-12 overflow-hidden rounded border bg-slate-50',
                                      useDefaultThumbs && 'border-violet-200',
                                    )}
                                  >
                                    {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : null}
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="align-top px-3 py-3">
                        <span
                          className={cn(
                            'inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1',
                            mpServingStatusBadgeClass(status),
                          )}
                        >
                          {mpServingStatusLabel(status, row.serving_status_label)}
                        </span>
                        {status === 'pending_review' && (row.matched_sensitive_words?.length ?? 0) > 0 ? (
                          <p className="mt-1 text-[10px] leading-snug text-violet-800">
                            命中：{row.matched_sensitive_words!.slice(0, 3).join('、')}
                            {(row.matched_sensitive_words?.length ?? 0) > 3 ? '…' : ''}
                          </p>
                        ) : null}
                      </td>
                      <td className="sticky right-0 border-l border-slate-100 bg-white/95 px-2 py-3 text-right align-top">
                        <div className="inline-flex flex-wrap justify-end gap-1">
                      <Button
                        type="button"
                        variant="secondary"
                        className="!px-2.5 !py-1 text-xs"
                        disabled={busyId === row.entry_id}
                        onClick={() => {
                          setDetailEntryId(row.entry_id);
                          setDetailOpen(true);
                        }}
                      >
                        详情
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="!px-2.5 !py-1 text-xs"
                        disabled={busyId === row.entry_id}
                        onClick={() => {
                          setFormEntryId(row.entry_id);
                          setFormOpen(true);
                        }}
                      >
                        编辑
                      </Button>
                      {status === 'pending_review' ? (
                        <>
                          <Button
                            type="button"
                            variant="primary"
                            className="!px-2.5 !py-1 text-xs"
                            disabled={busyId === row.entry_id}
                            onClick={() => void approveReview(row.entry_id)}
                          >
                            通过
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            className="!px-2.5 !py-1 text-xs text-rose-700"
                            disabled={busyId === row.entry_id}
                            onClick={() => void rejectReview(row.entry_id)}
                          >
                            拒绝
                          </Button>
                        </>
                      ) : null}
                      {status !== 'paused' && status !== 'pending_review' ? (
                        <Button
                          type="button"
                          variant="secondary"
                          className="!px-2.5 !py-1 text-xs"
                          disabled={busyId === row.entry_id}
                          onClick={() => void setServing(row.entry_id, 'paused')}
                        >
                          暂停
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="secondary"
                          className="!px-2.5 !py-1 text-xs"
                          disabled={busyId === row.entry_id}
                          onClick={() => void setServing(row.entry_id, 'active')}
                        >
                          恢复
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        className="!px-2.5 !py-1 text-xs"
                        disabled={busyId === row.entry_id}
                        onClick={() => void removeEntry(row.entry_id)}
                      >
                        删除
                      </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && total > 0 ? (
          <PaginationBar
            className="mt-4"
            page={page}
            totalPages={pages}
            total={total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(pages, p + 1))}
          />
        ) : null}
      </Card>

      <MicroPromotionTaskWebhookPanel areaId={areaId} infoType={infoType} />

      <Card title="最近 Webhook 调试">
        {webhookLoading ? (
          <Spinner label="加载日志" />
        ) : webhookItems.length === 0 ? (
          <p className="text-sm text-slate-500">暂无推送记录。</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-xs">
            {webhookItems.map((w) => (
              <li key={w.id} className="flex flex-wrap gap-2 py-2">
                <span className={w.result_ok ? 'text-emerald-700' : 'text-rose-600'}>{w.status}</span>
                <span className="text-slate-500">{w.created_at}</span>
                {w.form_code ? <span className="font-mono">{w.form_code}</span> : null}
                {w.serial_number ? <span>序 {w.serial_number}</span> : null}
                {w.error_code ? <span className="text-rose-600">{w.error_code}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <MicroPromotionEntryDetailModal
        open={detailOpen}
        entryId={detailEntryId}
        onClose={() => {
          setDetailOpen(false);
          setDetailEntryId(0);
        }}
        onReviewChanged={() => void load()}
      />
      <MicroPromotionEntryFormModal
        open={formOpen}
        areaId={areaId}
        infoType={infoType}
        entryId={formEntryId}
        onClose={() => {
          setFormOpen(false);
          setFormEntryId(0);
        }}
        onSaved={() => void load()}
      />
    </div>
  );
}
